import { API_ITEMS_CONFIGS } from "app/pages/sales-intelligence/modules/manage-integrations/constants/api-items";
import FindNewCompaniesGatewayButtonContainer from "app/pages/sales-intelligence/modules/manage-integrations/containers/FindNewCompaniesGatewayButtonContainer/FindNewCompaniesGatewayButtonContainer";
import SignalsGatewayButtonContainer from "app/pages/sales-intelligence/modules/manage-integrations/containers/SignalsGatewayButtonContainer/SignalsGatewayButtonContainer";
import SignalsRestApiGatewayButtonContainer from "app/pages/sales-intelligence/modules/manage-integrations/containers/SignalsRestApiGatewayButtonContainer/SignalsRestApiGatewayButtonContainer";
import type { ApiPageType } from "app/pages/sales-intelligence/modules/manage-integrations/types/api-page-type";
import {
    DYNAMICS_INTEGRATION_MANUAL_URL,
    FIND_NEW_COMPANIES_API_GUIDE_URL,
    HUBSPOT_INTEGRATION_MANUAL_URL,
    SALESFORCE_INTEGRATION_MANUAL_URL,
    SIGNALS_API_DOCS_URL,
    SIGNALS_REST_API_DOCS_URL,
} from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import {
    createGatewayAPIPageItemWithButton,
    createGatewayPageItem,
    createGatewayPageItemWithCRMConnect,
    createGatewaySignalsRestApiPageItem,
} from "app/pages/sales-intelligence/pages/management/utils/createGatewayPageItem";
import type { RequestAdminFeatureBase } from "app/pages/sales-intelligence/types/requestAdminFeature";

import { GATEWAY_BREADCRUMB_PARENT } from "../constants/gateway-breadcrumb-parent";
import type { IGatewayPageContent } from "../containers/GatewayPageContainer/types";

export const GATEWAY_TRACKING_NAME: Record<ApiPageType | RequestAdminFeatureBase, string> = {
    technologies: "Technologies API",
    leadEnrichment: "Lead Enrichment API",
    contacts: "Contacts API",
    aiOutreach: "AI Outreach API",
    signalsRestApi: "Signals REST API",
    salesforce: "Salesforce integration",
    hubspot: "Hubspot integration",
    dynamics: "Dynamics integration",
    findNewCompaniesWebhook: "Find new companies",
    signalsWebhook: "Signals API webhook",
};

export const DEFAULT_GATEWAY_PAGE_TITLE_KEY = "si.pages.management.gateway.title";

export const GATEWAY_PAGE_TITLE_KEY: Record<ApiPageType | RequestAdminFeatureBase, string> = {
    technologies: "si.pages.management.gateway.technologies.title",
    leadEnrichment: "si.pages.management.gateway.leadEnrichment.title",
    contacts: "si.pages.management.gateway.contacts.title",
    aiOutreach: "si.pages.management.gateway.aiOutreach.title",
    signalsRestApi: "si.pages.management.gateway.signalsRestApi.title",
    salesforce: "si.pages.management.gateway.salesforce.title",
    hubspot: "si.pages.management.gateway.hubspot.title",
    dynamics: "si.pages.management.gateway.salesforce.title",
    signalsWebhook: "si.pages.management.gateway.signalsWebhook.title",
    findNewCompaniesWebhook: "si.pages.management.gateway.findNewCompaniesWebhook.title",
};

export const HOW_DOES_IT_WORK = "si.manage-integrations.gateway.common.how-does-it-work";

export const CONTENT_BY_PAGE_TYPE: Partial<Record<ApiPageType | RequestAdminFeatureBase, IGatewayPageContent>> = {
    technologies: createGatewayAPIPageItemWithButton("technologies", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.technologies.title",
        descriptionKey: "si.manage-integrations.gateway.technologies.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.technologies.info-item1",
            "si.manage-integrations.gateway.technologies.info-item2",
            "si.manage-integrations.gateway.technologies.info-item3",
        ],
        creditsItemsKeys: ["si.manage-integrations.gateway.technologies.credits-item1"],
        tutorialLink: API_ITEMS_CONFIGS.technologies.tutorialLink,
    }),
    leadEnrichment: createGatewayAPIPageItemWithButton("leadEnrichment", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.leadEnrichment.title",
        descriptionKey: "si.manage-integrations.gateway.leadEnrichment.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.leadEnrichment.info-item1",
            "si.manage-integrations.gateway.leadEnrichment.info-item2",
            "si.manage-integrations.gateway.leadEnrichment.info-item3",
        ],
        creditsItemsKeys: [
            "si.manage-integrations.gateway.leadEnrichment.credits-item1",
            "si.manage-integrations.gateway.leadEnrichment.credits-item2",
            "si.manage-integrations.gateway.leadEnrichment.credits-item3",
        ],
        tutorialLink: API_ITEMS_CONFIGS.leadEnrichment.tutorialLink,
    }),
    contacts: createGatewayAPIPageItemWithButton("contacts", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.contacts.title",
        descriptionKey: "si.manage-integrations.gateway.contacts.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.contacts.info-item1",
            "si.manage-integrations.gateway.contacts.info-item2",
            "si.manage-integrations.gateway.contacts.info-item3",
        ],
        creditsItemsKeys: [
            "si.manage-integrations.gateway.contacts.credits-item1",
            "si.manage-integrations.gateway.contacts.credits-item2",
        ],
        tutorialLink: API_ITEMS_CONFIGS.contacts.tutorialLink,
    }),
    aiOutreach: createGatewayAPIPageItemWithButton("aiOutreach", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.aiOutreach.title",
        descriptionKey: "si.manage-integrations.gateway.aiOutreach.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.aiOutreach.info-item1",
            "si.manage-integrations.gateway.aiOutreach.info-item2",
            "si.manage-integrations.gateway.aiOutreach.info-item3",
        ],
        creditsItemsKeys: ["si.manage-integrations.gateway.aiOutreach.credits-item1"],
        tutorialLink: API_ITEMS_CONFIGS.aiOutreach.tutorialLink,
    }),
    signalsRestApi: createGatewaySignalsRestApiPageItem("signalsRestApi", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.signalsRestApi.title",
        descriptionKey: "si.manage-integrations.gateway.signalsRestApi.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.signalsRestApi.info-item1",
            "si.manage-integrations.gateway.signalsRestApi.info-item2",
            "si.manage-integrations.gateway.signalsRestApi.info-item3",
        ],
        creditsItemsKeys: [
            "si.manage-integrations.gateway.signalsRestApi.credits-item1",
            "si.manage-integrations.gateway.signalsRestApi.credits-item2",
        ],
        tutorialLink: SIGNALS_REST_API_DOCS_URL,
        renderStickyActionButton: () => <SignalsRestApiGatewayButtonContainer variant="gateway" />,
    }),
    salesforce: createGatewayPageItemWithCRMConnect("salesforce", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.crmIntegrations,
        titleKey: "si.manage-integrations.gateway.salesforce.title",
        descriptionKey: "si.manage-integrations.gateway.salesforce.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.salesforce.info-item1",
            "si.manage-integrations.gateway.salesforce.info-item2",
            "si.manage-integrations.gateway.salesforce.info-item3",
        ],
        creditsItemsKeys: [
            "si.manage-integrations.gateway.salesforce.credits-item1",
            "si.manage-integrations.gateway.salesforce.credits-item2",
            "si.manage-integrations.gateway.salesforce.credits-item3",
        ],
        tutorialLink: SALESFORCE_INTEGRATION_MANUAL_URL,
    }),
    hubspot: createGatewayPageItemWithCRMConnect("hubspot", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.crmIntegrations,
        titleKey: "si.manage-integrations.gateway.hubspot.title",
        descriptionKey: "si.manage-integrations.gateway.hubspot.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.hubspot.info-item1",
            "si.manage-integrations.gateway.hubspot.info-item2",
            "si.manage-integrations.gateway.hubspot.info-item3",
        ],
        creditsItemsKeys: [
            "si.manage-integrations.gateway.hubspot.credits-item1",
            "si.manage-integrations.gateway.hubspot.credits-item2",
            "si.manage-integrations.gateway.hubspot.credits-item3",
        ],
        tutorialLink: HUBSPOT_INTEGRATION_MANUAL_URL,
    }),
    dynamics: createGatewayPageItemWithCRMConnect("dynamics", {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.crmIntegrations,
        titleKey: "si.manage-integrations.gateway.dynamics.title",
        descriptionKey: "si.manage-integrations.gateway.dynamics.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.dynamics.info-item1",
            "si.manage-integrations.gateway.dynamics.info-item2",
            "si.manage-integrations.gateway.dynamics.info-item3",
        ],
        creditsItemsKeys: ["si.manage-integrations.gateway.dynamics.credits-item1"],
        tutorialLink: DYNAMICS_INTEGRATION_MANUAL_URL,
    }),
    signalsWebhook: createGatewayPageItem({
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.signalsWebhook.title",
        descriptionKey: "si.manage-integrations.gateway.signalsWebhook.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.signalsWebhook.info-item1",
            "si.manage-integrations.gateway.signalsWebhook.info-item2",
            "si.manage-integrations.gateway.signalsWebhook.info-item3",
        ],
        creditsItemsKeys: ["si.manage-integrations.gateway.signalsWebhook.credits-item1"],
        tutorialLink: SIGNALS_API_DOCS_URL,
        renderActionButton: () => <SignalsGatewayButtonContainer />,
    }),
    findNewCompaniesWebhook: createGatewayPageItem({
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.apisAndWebhooks,
        titleKey: "si.manage-integrations.gateway.findNewCompaniesWebhook.title",
        descriptionKey: "si.manage-integrations.gateway.findNewCompaniesWebhook.description",
        infoItemsKeys: [
            "si.manage-integrations.gateway.findNewCompaniesWebhook.info-item1",
            "si.manage-integrations.gateway.findNewCompaniesWebhook.info-item2",
            "si.manage-integrations.gateway.findNewCompaniesWebhook.info-item3",
        ],
        creditsItemsKeys: ["si.manage-integrations.gateway.findNewCompaniesWebhook.credits-item1"],
        tutorialLink: FIND_NEW_COMPANIES_API_GUIDE_URL,
        renderActionButton: () => <FindNewCompaniesGatewayButtonContainer />,
    }),
};
